import React, { useCallback } from 'react';
import ReactGA from 'react-ga';
import { Link } from 'react-router-dom';
import config from '~/config';
import { useDispatch, useSelector } from '~/redux';
import { openModal } from '~/redux/actions';
import { getModuleByKey } from '~/utils/trainingModules';
import TrainingModuleModal from '~/components/modals/TrainingModuleModal';
import LeavingPromptModal from '~/components/modals/LeavingPromptModal';

import { absoluteCenter, absoluteFill, breakpoints, calcRem, colors, css, fluidRange, fontSmoothing, respondTo, styled, transitionDefault } from '~/styles';

import stripHTML from '~/utils/stripHTML';


const StyledComponentContainer = styled.a`
  display: block;
  flex: 1 1 0;
  position: relative;
  text-align: left;
  background-color: rgba(0, 0, 0, 0.75);
  height: ${calcRem(300)};
  max-height: 35vh;

  ${respondTo(
    breakpoints.lg,
    css`
      height: 50vw;
      max-height: 70vh; /* landscape! */
    `
  )}
  
  // gradient
  &:after {

  }

  // hover overlay
  &:before {
    ${absoluteFill};
    content: '';
    display: block;
    background-color: rgba(3,55,77,0.5);
    z-index: 1;
    opacity: 0;
    visibility: hidden;
    ${transitionDefault};
  }

  &:hover {
    &:before {
      opacity: 1;
      visibility: visible;
    }
  }

  &.inactive {
    pointer-events: none;
    opacity: 1;

    &:before {
      background-color: black;
      opacity: 0.7;
      visibility: visible;
      z-index: 3;
    }
  }
`

const StyledTitle = styled.h5`
  position: absolute;
  top: 0;
  left: 0;
  width: 67%;
  background: ${colors.black};
  color: ${colors.white};
  padding: 0.35em 10px 0.3em 0;
  ${fluidRange('paddingLeft', '20px', '30px', breakpoints.xs, breakpoints.lg)}
  /* ${fluidRange('fontSize', '16px', '21.5px', breakpoints.xs, breakpoints.lg)} */
  font-size: ${calcRem(21.5)};
  font-weight: 500;
  z-index: 2;
`

const StyledDescription = styled.p`
  /* ${fluidRange('fontSize', '14px', '20.5px', breakpoints.xs, breakpoints.lg)} */
  font-size: 1em;
  position: absolute;
  ${fluidRange('bottom', '20px', '25px', breakpoints.xs, breakpoints.lg)}
  right: 20%;  // TODO: fix for arrow
  ${fluidRange('left', '20px', '30px', breakpoints.xs, breakpoints.lg)}
  line-height: 1.39;
  text-decoration: none;
  color: ${colors.white};
  margin-bottom: 0;
  z-index: 2;
`

const BackgroundImageCont = styled.div`
  ${absoluteFill};  
  background-size: cover;
  background-repeat: no-repeat; 
  background-position: center center;
`

const ComingSoon = styled.div`
  ${absoluteCenter};
  width: 100%;
  text-transform: uppercase;
  font-size: ${calcRem(50)};
  padding: 0 10%;
  color: ${colors.white};
  z-index: 4;
  font-weight: 300;
  letter-spacing: 0.25em;
  text-align: center;
  ${fontSmoothing};
`


interface Props extends React.HTMLAttributes<HTMLAnchorElement> {
  title: string;
  description?: string;
  bgImg?: string;
  bgImgAlign?: string;
  inactive?: boolean;

  to?: string;
  as?: React.ElementType;
  href?: string;
  target?: string;
  module?: string;
  externalLink?: string;
}
const BottomBlockLink: React.FC<Props> = ({ title, description, bgImg, bgImgAlign, inactive, as,  ...props }) => {
  // redux state
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const ga = useCallback(event => {
    event.stopPropagation();
    
    if (config.debug) { console.log('click bottom block link', { title, description, bgImg, bgImgAlign, inactive, as,  ...props } ); }

    if (as && as === 'a') {
      if (props.href && !props.externalLink) {
        ReactGA.event({
          category: 'Link',
          action: 'Click',
          label: stripHTML(title)
        })
      }

      if (props.module) {
        if (config.debug) { console.log('open module', props.module); }

        const module = getModuleByKey(props.module);
        if (module) {
          let parsedBaseURL = module.path;
          if (module.pathMode === 'absolute') {
            parsedBaseURL = window.location.protocol + '//' + window.location.host + parsedBaseURL;
          }
          const url = parsedBaseURL + '?sid=' + user?.uid + '&cmid=' + props.module;

          dispatch(openModal({
            content: <TrainingModuleModal config={{ url }} />,
            mode: 'fullscreen'
          }));
  
          ReactGA.event({
            category: 'Module',
            action: 'Open',
            label: stripHTML(title)
          })
        }
      }

      if (props.externalLink === 'true') {
        event.preventDefault();

        if (config.debug) { console.log('open external link', props.href, description); }

        dispatch(openModal({
          content: <LeavingPromptModal link={props.href || ''} title={description || ''} />,
          mode: 'minimal'
        }));

        // analytics handled by LeavingPromptModal component
      }
    }
  }, [dispatch]);

  const backgroundImageStyle = () => {
    return { 
      backgroundImage: `url(${bgImg})`,
      backgroundPosition: `${bgImgAlign ? bgImgAlign : 'center'} center`
    }
  }

  return (
      <StyledComponentContainer as={as || Link} {...props} className={`
        ${inactive ? 'inactive' : ''}
      `}
      onClick={event => ga(event)}
      >
        {
          !!bgImg && 
          <BackgroundImageCont style={ backgroundImageStyle() } />
        }
        <StyledTitle dangerouslySetInnerHTML={{ __html: title }} />
        { 
          !!description && 
          <StyledDescription dangerouslySetInnerHTML={{ __html: description }} />
        }
        {
          !!inactive && <ComingSoon>Coming soon</ComingSoon>
        }
      </StyledComponentContainer>
  )
}
export default BottomBlockLink;


// WEBPACK FOOTER //
// ./src/components/layout/BottomBlockLink.tsx